import { useState } from 'react'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ChevronRight, Home, BarChart3, Folder, Settings, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import type { UserBizRole } from '@/utils/user-role'

interface SidebarProps {
  currentMenu: string
  onNavigate: (menu: string) => void
  collapsed?: boolean
  onToggle?: () => void
  version?: string
  currentUser?: string
  userBizRole?: UserBizRole
}

type MenuItem = {
  id: string
  label: string
  icon: React.ReactNode
  children?: { id: string; label: string }[]
}

const MENU_ITEMS: MenuItem[] = [
  {
    id: 'home',
    label: '首页',
    icon: <Home className="w-4 h-4" />,
  },
  {
    id: 'analysis',
    label: '业务分析中心',
    icon: <BarChart3 className="w-4 h-4" />,
    children: [
      { id: 'templates', label: '报表模板库' },
      { id: 'topics', label: '业务主题库' },
      { id: 'cases', label: '经典案例' },
    ],
  },
  {
    id: 'ops-center',
    label: '业务支持中心',
    icon: <Settings className="w-4 h-4" />,
    children: [
      { id: 'user-center', label: '用户中心' },
      { id: 'admin-center', label: '服务中心' },
    ],
  },
  {
    id: 'myspace',
    label: '我的空间',
    icon: <Folder className="w-4 h-4" />,
  },
]

export function Sidebar({ currentMenu, onNavigate, collapsed, onToggle, version, currentUser, userBizRole }: SidebarProps) {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set(['analysis', 'ops-center']))

  // v1.0: 业务分析中心仅保留共享模板；业务支持中心仅金融科技部运营可见服务中心，隐藏用户中心
  const menuItems = version === '1'
    ? MENU_ITEMS.map(item => {
        if (item.id === 'analysis') {
          const canSeeTopics = currentUser === '金融科技部运营'
          return { ...item, children: item.children?.filter(c => c.id === 'templates' || (c.id === 'topics' && canSeeTopics)) }
        }
        if (item.id === 'ops-center') {
          const canSeeAdmin = currentUser === '金融科技部运营'
          const canSeeUserCenter = currentUser !== '金融科技部运营'
          return { ...item, children: item.children?.filter(c => (c.id === 'user-center' && canSeeUserCenter) || (c.id === 'admin-center' && canSeeAdmin)) }
        }
        return item
      })
    : MENU_ITEMS

  const toggleExpanded = (id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <aside className={cn(
      "border-r bg-white flex flex-col shadow-sm transition-all duration-200 shrink-0",
      collapsed ? "w-14" : "w-64"
    )}>
      {/* 收起/展开按钮 - 顶部 */}
      <div className="border-b shrink-0">
        <button
          className={cn(
            "flex items-center justify-center w-full py-2 cursor-pointer transition-colors text-gray-400 hover:text-gcb-primary hover:bg-gray-50",
          )}
          onClick={onToggle}
          title={collapsed ? '展开菜单' : '收起菜单'}
        >
          {collapsed
            ? <PanelLeftOpen className="w-4 h-4" />
            : <PanelLeftClose className="w-4 h-4" />
          }
        </button>
      </div>

      {/* 菜单列表 */}
      <ScrollArea className="flex-1">
        <div className={cn("py-2", collapsed ? "px-1" : "space-y-1 px-2")}>
          {menuItems.map((item) => {
            // 可展开菜单
            if (item.children) {
              const isExpanded = expandedItems.has(item.id)
              const isActive = currentMenu === item.id || item.children.some(c => c.id === currentMenu)

              if (collapsed) {
                return (
                  <div key={item.id} className="mb-1">
                    <div
                      className={cn(
                        "flex items-center justify-center py-2 rounded-md cursor-pointer transition-colors",
                        isActive && "bg-blue-50 text-gcb-primaryDark"
                      )}
                      onClick={() => onNavigate(item.children[0].id)}
                      title={item.label}
                    >
                      {item.icon}
                    </div>
                  </div>
                )
              }

              return (
                <div key={item.id}>
                  <Collapsible open={isExpanded} onOpenChange={() => toggleExpanded(item.id)}>
                    <CollapsibleTrigger asChild>
                      <div
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors text-sm",
                          isActive && "bg-blue-50 text-gcb-primaryDark"
                        )}
                      >
                        <ChevronRight
                          className={cn(
                            "w-3.5 h-3.5 shrink-0 text-gray-400 transition-transform duration-200",
                            isExpanded && "rotate-90"
                          )}
                        />
                        {item.icon}
                        <span className={cn(
                          "transition-colors",
                          isActive ? "font-medium text-gcb-primaryDark" : "text-gray-700"
                        )}>
                          {item.label}
                        </span>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="data-[state=open]:animate-slide-in">
                      <div className="ml-6 mt-1 space-y-1 border-l-2 border-gray-100 pl-3">
                        {item.children.map(child => {
                          const isChildActive = currentMenu === child.id
                          return (
                            <div
                              key={child.id}
                              className={cn(
                                "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors text-sm",
                                isChildActive
                                  ? "bg-blue-50 text-gcb-primaryDark font-medium"
                                  : "text-gray-600 hover:bg-gray-50"
                              )}
                              onClick={() => onNavigate(child.id)}
                            >
                              <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                              <span>{child.label}</span>
                            </div>
                          )
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              )
            }

            // 普通菜单项
            const isActive = currentMenu === item.id

            if (collapsed) {
              return (
                <div
                  key={item.id}
                  className={cn(
                    "flex items-center justify-center py-2 mb-1 rounded-md cursor-pointer transition-colors",
                    isActive && "bg-blue-50 text-gcb-primaryDark"
                  )}
                  onClick={() => onNavigate(item.id)}
                  title={item.label}
                >
                  {item.icon}
                </div>
              )
            }

            return (
              <div
                key={item.id}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors text-sm",
                  isActive
                    ? "bg-blue-50 text-gcb-primaryDark font-medium"
                    : "text-gray-700 hover:bg-gray-50"
                )}
                onClick={() => onNavigate(item.id)}
              >
                <div className="w-5 flex items-center justify-center">{item.icon}</div>
                <span>{item.label}</span>
              </div>
            )
          })}
        </div>
      </ScrollArea>
    </aside>
  )
}
